/**
 * Cart drawer upsells
 * - <cart-upsell-form>: adds a product via /cart/add.js and refreshes the drawer without leaving it
 * - <cart-drawer-recommendations>: swaps collection fallback for Shopify complementary products
 */

const SECTION_ID = 'cart-drawer-section';
const ROOT = window.Shopify?.routes?.root || '/';

/** Tell Horizon the cart changed so the drawer, header count and cart page all re-render. */
async function announceCartUpdate(source, cart, sections, productId) {
  try {
    // Verify this constructor against assets/events.js in your Horizon version.
    const { CartAddEvent } = await import('@theme/events');
    source.dispatchEvent(
      new CartAddEvent(cart, 'cart-upsell', {
        source: 'cart-upsell',
        itemCount: cart.item_count,
        productId,
        sections,
      })
    );
    return;
  } catch (error) {
    console.warn('[cart-upsell] CartAddEvent unavailable, using fallback render', error);
  }

  // Fallback: replace the drawer contents directly with the fresh section HTML.
  const html = sections?.[SECTION_ID];
  if (!html) return;
  const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer__inner');
  const current = document.querySelector('#cart-drawer .cart-drawer__inner');
  if (fresh && current) current.replaceWith(fresh);
}

class CartUpsellForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  onSubmit = async (event) => {
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    const error = this.form.querySelector('.cart-upsell-card__error');
    if (button?.getAttribute('aria-busy') === 'true') return;

    button?.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    const formData = new FormData(this.form);
    formData.append('sections', SECTION_ID);
    formData.append('sections_url', window.location.pathname);

    try {
      const addResponse = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const added = await addResponse.json();

      if (!addResponse.ok) {
        throw new Error(added.description || added.message || 'Could not add this product.');
      }

      const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      await announceCartUpdate(this, cart, added.sections, added.product_id);
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };
}

/** Cache per URL so a drawer re-render doesn't refetch the same recommendations. */
const recsCache = new Map();

class CartDrawerRecommendations extends HTMLElement {
  static observedAttributes = ['data-url'];

  connectedCallback() {
    this.track = this.querySelector('[data-upsell-track]');
    this.updateVisibility();

    // Horizon morphs the drawer on every cart change; re-apply cached recs if they get overwritten.
    this.observer = new MutationObserver(() => {
      if (this.applying) return;
      this.track = this.querySelector('[data-upsell-track]');
      const cached = recsCache.get(this.dataset.url);
      if (cached && this.track && this.track.dataset.recsApplied !== this.dataset.url) this.apply(cached);
      else this.updateVisibility();
    });
    this.observer.observe(this, { childList: true, subtree: true });

    this.load();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) this.load();
  }

  async load() {
    const url = this.dataset.url;
    if (!url) return this.updateVisibility(); // collection mode: server-rendered only

    if (recsCache.has(url)) return this.apply(recsCache.get(url));

    try {
      const text = await fetch(url).then((r) => r.text());
      const track = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-recommendations-track]');
      const html = track?.innerHTML.trim() || '';
      recsCache.set(url, html);
      this.apply(html);
    } catch (error) {
      console.warn('[cart-upsell] recommendations failed, keeping collection fallback', error);
      this.updateVisibility();
    }
  }

  apply(html) {
    this.track = this.querySelector('[data-upsell-track]');
    if (!this.track) return;
    // Empty complementary result → keep the collection fallback that is already rendered.
    if (html) {
      this.applying = true;
      this.track.innerHTML = html;
      this.track.dataset.recsApplied = this.dataset.url;
      this.track.scrollLeft = 0;
      queueMicrotask(() => (this.applying = false));
    }
    this.updateVisibility();
  }

  updateVisibility() {
    const hasItems = !!this.querySelector('[data-upsell-track] .cart-upsell-card');
    this.hidden = !hasItems;
  }
}

if (!customElements.get('cart-upsell-form')) customElements.define('cart-upsell-form', CartUpsellForm);
if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}